import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  LayoutGrid,
  MoreHorizontal,
  Search,
  Settings,
  Shield,
  User,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { Permission } from '../types';
import {
  buildPermissionTree,
  checkedPermissionIds,
  permissionCheckState,
  togglePermissionNode,
  type PermissionCheckState,
  type PermissionNode,
} from '../permissions/permissionAccess';

interface PermissionTreeEditorProps {
  permissions: Permission[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  roleKey: string;
}

type ScopeId = 'user' | 'admin';
type CategoryId = 'all' | 'employees' | 'scheduling' | 'time' | 'reports' | 'settings' | 'other';

const SCOPES: { id: ScopeId; label: string; icon: LucideIcon }[] = [
  { id: 'user', label: 'User', icon: User },
  { id: 'admin', label: 'Admin', icon: Shield },
];

const CATEGORIES: { id: CategoryId; label: string; icon: LucideIcon }[] = [
  { id: 'all', label: 'All Permissions', icon: LayoutGrid },
  { id: 'employees', label: 'Employees', icon: Users },
  { id: 'scheduling', label: 'Scheduling', icon: Calendar },
  { id: 'time', label: 'Time & Attendance', icon: Clock },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'other', label: 'Other', icon: MoreHorizontal },
];

const USER_CODES = new Set([
  'dashboard',
  'clock_in_out',
  'holidays',
  'incidents',
  'my_trainings',
  'my_schedules',
  'my_attendances',
  'my_tasks',
  'my_messages',
  'my_licenses',
  'marketplace_proposals',
  'chat_assistant',
  'real_time_transcription',
]);

const CODE_CATEGORY: Record<string, Exclude<CategoryId, 'all' | 'other'>> = {
  employee_management: 'employees',
  my_licenses: 'employees',
  documents_policies: 'employees',
  job_applicant_tracking: 'employees',
  feedback: 'employees',
  training: 'employees',
  my_trainings: 'employees',
  holidays: 'employees',
  holiday_management: 'employees',
  incidents: 'employees',
  incident_management: 'employees',
  my_tasks: 'employees',
  task_management: 'employees',
  my_messages: 'employees',
  message_management: 'employees',
  chat_assistant: 'employees',
  ask_staffly_admin: 'employees',
  scheduling: 'scheduling',
  my_schedules: 'scheduling',
  marketplace_proposals: 'scheduling',
  attendance: 'time',
  my_attendances: 'time',
  clock_in_out: 'time',
  time_station_clock_in_out: 'time',
  security_command_control_log: 'time',
  payroll: 'settings',
  location_selector: 'settings',
  qa_checks: 'settings',
};

const TONES = [
  { bg: 'bg-blue-50', text: 'text-blue-600' },
  { bg: 'bg-violet-50', text: 'text-violet-600' },
  { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  { bg: 'bg-orange-50', text: 'text-orange-600' },
  { bg: 'bg-amber-50', text: 'text-amber-600' },
  { bg: 'bg-sky-50', text: 'text-sky-600' },
];

function categoryForCode(code: string | undefined): Exclude<CategoryId, 'all'> {
  const value = code ?? '';
  if (CODE_CATEGORY[value]) return CODE_CATEGORY[value];
  if (value === 'reports' || value.startsWith('reports_')) return 'reports';
  if (value === 'settings' || value.startsWith('settings_')) return 'settings';
  if (value.startsWith('my_')) return 'employees';
  return 'other';
}

function scopeForCode(code: string | undefined): ScopeId {
  const value = code ?? '';
  if (USER_CODES.has(value) || value.startsWith('my_')) return 'user';
  return 'admin';
}

function nodeIds(node: PermissionNode): number[] {
  return [node.permission.id, ...node.children.flatMap(nodeIds)];
}

function nodeText(node: PermissionNode): string {
  const own = `${node.permission.name} ${node.permission.description ?? ''} ${node.permission.code}`.toLowerCase();
  return `${own} ${node.children.map(nodeText).join(' ')}`;
}

function parentIdsWithChildren(nodes: PermissionNode[]): number[] {
  return nodes.flatMap((node) => [
    ...(node.children.length > 0 ? [node.permission.id] : []),
    ...parentIdsWithChildren(node.children),
  ]);
}

const PermissionTreeEditor: React.FC<PermissionTreeEditorProps> = ({ permissions, selectedIds, onChange, roleKey }) => {
  const tree = useMemo(() => buildPermissionTree(permissions), [permissions]);
  const checked = useMemo(() => checkedPermissionIds(permissions, selectedIds), [permissions, selectedIds]);
  const [scope, setScope] = useState<ScopeId>('user');
  const [category, setCategory] = useState<CategoryId>('all');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const next: Record<number, boolean> = {};
    const walk = (node: PermissionNode) => {
      if (node.children.length === 0) return;
      next[node.permission.id] = permissionCheckState(node, checked) !== 'off';
      node.children.forEach(walk);
    };
    tree.forEach(walk);
    setExpanded(next);
    setQuery('');
    setScope('user');
    setCategory('all');
    // Open groups that already have a selection when the role being edited changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey, permissions]);

  const scopedTree = useMemo(
    () => tree.filter((node) => scopeForCode(node.permission.code) === scope),
    [tree, scope]
  );

  const cards = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return scopedTree.filter((node) => {
      if (category !== 'all' && categoryForCode(node.permission.code) !== category) return false;
      if (!needle) return true;
      return nodeText(node).includes(needle);
    });
  }, [scopedTree, category, query]);

  const selectedCategoryNodes = useMemo(
    () =>
      scopedTree.filter((node) => {
        if (category !== 'all' && categoryForCode(node.permission.code) !== category) return false;
        return permissionCheckState(node, checked) !== 'off';
      }),
    [scopedTree, category, checked]
  );

  const counts = useMemo(() => {
    const byCategory = new Map<CategoryId, { selected: number; total: number }>();
    const byScope = { user: { selected: 0, total: 0 }, admin: { selected: 0, total: 0 } };
    for (const item of CATEGORIES) byCategory.set(item.id, { selected: 0, total: 0 });
    for (const node of tree) {
      const ids = nodeIds(node);
      const selected = ids.filter((id) => checked.has(id)).length;
      const nodeScope = scopeForCode(node.permission.code);
      byScope[nodeScope].total += ids.length;
      byScope[nodeScope].selected += selected;
      if (nodeScope !== scope) continue;
      const bucket = categoryForCode(node.permission.code);
      const add = (id: CategoryId) => {
        const row = byCategory.get(id)!;
        row.total += ids.length;
        row.selected += selected;
      };
      add('all');
      add(bucket);
    }
    return { byCategory, byScope };
  }, [tree, checked, scope]);

  const visibleCategories = CATEGORIES.filter((item) => {
    const total = counts.byCategory.get(item.id)?.total ?? 0;
    if (item.id === 'all') return true;
    return total > 0;
  });
  const categoryIds = (category === 'all' ? scopedTree : scopedTree.filter((node) => categoryForCode(node.permission.code) === category)).flatMap(nodeIds);
  const categorySelected = categoryIds.filter((id) => checked.has(id)).length;
  const filteredIds = cards.flatMap(nodeIds);
  const filteredSelected = filteredIds.filter((id) => checked.has(id)).length;
  const expandableIds = parentIdsWithChildren(cards);
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expanded[id]);
  const activeCategoryLabel = CATEGORIES.find((item) => item.id === category)?.label ?? 'Permissions';

  if (permissions.length === 0) {
    return <p className="text-sm text-neutral-500 text-center py-4">No permissions available</p>;
  }

  return (
    <div className="grid h-full min-h-[440px] grid-cols-1 overflow-hidden rounded-lg border border-neutral-200 bg-white lg:grid-cols-[220px_minmax(0,1fr)_240px]">
      <aside className="flex min-h-0 flex-col border-b border-neutral-200 bg-white lg:border-b-0 lg:border-r">
        <div className="space-y-3 p-3">
          <div className="flex rounded-full border border-neutral-200 bg-neutral-50 p-0.5">
            {SCOPES.map((item) => {
              const Icon = item.icon;
              const active = scope === item.id;
              const selected = counts.byScope[item.id].selected;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    setScope(item.id);
                    setCategory('all');
                  }}
                  className={`flex min-w-0 flex-1 items-center justify-center gap-1 rounded-full px-2 py-1.5 text-xs font-medium ${
                    active ? 'bg-blue-600 text-white shadow-sm' : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  {active ? <Check className="h-3.5 w-3.5 shrink-0" /> : <Icon className="h-3.5 w-3.5 shrink-0" />}
                  <span className="truncate">
                    {item.label} ({selected})
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search permissions..."
              className="w-full rounded-md border border-neutral-200 bg-neutral-50 py-2 pl-9 pr-3 text-sm text-neutral-800 placeholder:text-neutral-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-2 pb-3">
          {visibleCategories.map((item) => {
            const Icon = item.icon;
            const count = counts.byCategory.get(item.id) ?? { selected: 0, total: 0 };
            const active = category === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setCategory(item.id)}
                className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm ${
                  active ? 'bg-blue-50 font-medium text-blue-700' : 'text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-blue-600' : 'text-neutral-500'}`} />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                <span className={`text-xs ${active ? 'text-blue-600' : 'text-neutral-400'}`}>
                  {count.selected}/{count.total}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      <section className="flex min-h-0 min-w-0 flex-col bg-neutral-50">
        <div className="flex items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
          <p className="text-sm font-medium text-neutral-800">
            {filteredSelected} of {filteredIds.length} permissions selected
          </p>
          <button
            type="button"
            disabled={expandableIds.length === 0}
            onClick={() => {
              const open = !allExpanded;
              setExpanded((prev) => {
                const next = { ...prev };
                expandableIds.forEach((id) => {
                  next[id] = open;
                });
                return next;
              });
            }}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:cursor-not-allowed disabled:text-neutral-300"
          >
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {cards.length === 0 ? (
            <p className="py-8 text-center text-sm text-neutral-500">No permissions match your search.</p>
          ) : (
            cards.map((node, index) => (
              <PermissionCard
                key={node.permission.id}
                node={node}
                tone={TONES[index % TONES.length]}
                checked={checked}
                expanded={Boolean(query.trim()) || Boolean(expanded[node.permission.id])}
                onToggleExpanded={() =>
                  setExpanded((prev) => ({ ...prev, [node.permission.id]: !prev[node.permission.id] }))
                }
                onToggle={(item, turnOn) => onChange(togglePermissionNode(permissions, selectedIds, item, turnOn))}
              />
            ))
          )}
        </div>
      </section>

      <aside className="flex min-h-0 flex-col border-t border-neutral-200 bg-neutral-50 lg:border-l lg:border-t-0">
        <div className="border-b border-neutral-200 px-4 py-3">
          <h4 className="text-sm font-semibold text-neutral-900">Selected Permissions</h4>
          <p className="mt-1 text-xs text-neutral-500">{activeCategoryLabel}</p>
          <p className="mt-2 text-2xl font-semibold tracking-tight text-blue-700">
            {categorySelected}
            <span className="ml-1 text-base font-medium text-neutral-400">of {categoryIds.length}</span>
          </p>
        </div>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
          {selectedCategoryNodes.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-neutral-500">No permissions selected</p>
          ) : (
            selectedCategoryNodes.map((node) => (
              <SelectedBranch key={node.permission.id} node={node} checked={checked} depth={0} />
            ))
          )}
        </div>
      </aside>
    </div>
  );
};

function PermissionCard({
  node,
  tone,
  checked,
  expanded,
  onToggleExpanded,
  onToggle,
}: {
  node: PermissionNode;
  tone: { bg: string; text: string };
  checked: Set<number>;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggle: (node: PermissionNode, turnOn: boolean) => void;
}) {
  const state = permissionCheckState(node, checked);
  const hasChildren = node.children.length > 0;
  const Icon = categoryIcon(categoryForCode(node.permission.code));

  return (
    <div className="rounded-xl border border-neutral-200 bg-white px-3 py-3 shadow-sm">
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone.bg} ${tone.text}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <label htmlFor={`permission-${node.permission.id}`} className="block cursor-pointer text-sm font-semibold text-neutral-900">
            {node.permission.name}
          </label>
          {node.permission.description && (
            <p className="mt-0.5 text-xs leading-5 text-neutral-500">{node.permission.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          <TriStateCheckbox
            id={`permission-${node.permission.id}`}
            state={state}
            onChange={() => onToggle(node, state !== 'on')}
          />
          {hasChildren ? (
            <button
              type="button"
              className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
              aria-label={expanded ? `Collapse ${node.permission.name}` : `Expand ${node.permission.name}`}
              onClick={onToggleExpanded}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="w-6" />
          )}
        </div>
      </div>
      {hasChildren && expanded && (
        <div className="ml-12 mt-2 space-y-1 border-l border-neutral-200 pl-3">
          {node.children.map((child) => (
            <ChildRow key={child.permission.id} node={child} checked={checked} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
}

function ChildRow({
  node,
  checked,
  onToggle,
}: {
  node: PermissionNode;
  checked: Set<number>;
  onToggle: (node: PermissionNode, turnOn: boolean) => void;
}) {
  const state = permissionCheckState(node, checked);
  const hasChildren = node.children.length > 0;
  return (
    <div>
      <div className="flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-50">
        <TriStateCheckbox
          id={`permission-${node.permission.id}`}
          state={state}
          onChange={() => onToggle(node, state !== 'on')}
        />
        <label htmlFor={`permission-${node.permission.id}`} className="cursor-pointer text-sm text-neutral-800">
          {node.permission.name}
        </label>
      </div>
      {hasChildren && (
        <div className="ml-6 border-l border-neutral-200 pl-2">
          {node.children.map((child) => (
            <ChildRow key={child.permission.id} node={child} checked={checked} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
}

function SelectedBranch({ node, checked, depth }: { node: PermissionNode; checked: Set<number>; depth: number }) {
  const state = permissionCheckState(node, checked);
  if (state === 'off') return null;
  return (
    <div>
      <div className="flex items-start gap-2 py-1" style={{ paddingLeft: depth * 12 }}>
        {depth === 0 ? (
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
        ) : (
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-400" />
        )}
        <span className={`text-sm ${depth === 0 ? 'font-medium text-neutral-800' : 'text-neutral-600'}`}>
          {node.permission.name}
        </span>
      </div>
      {node.children.map((child) => (
        <SelectedBranch key={child.permission.id} node={child} checked={checked} depth={depth + 1} />
      ))}
    </div>
  );
}

function categoryIcon(category: Exclude<CategoryId, 'all'>): LucideIcon {
  return CATEGORIES.find((item) => item.id === category)?.icon ?? MoreHorizontal;
}

function TriStateCheckbox({
  id,
  state,
  onChange,
}: {
  id: string;
  state: PermissionCheckState;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'partial';
  }, [state]);

  return (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={state === 'on'}
      onChange={onChange}
      className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
    />
  );
}

export default PermissionTreeEditor;
